/**
 * welcomeGate.js — правило «когда снимать стартовую заставку». [10.19.22]
 *
 * ПОВОД, найденный чтением. Заставка (`#cinematic-welcome`) из программы никуда
 * не пропадала — её СНИМАЛИ на первой же миллисекунде. Снятие висело на
 * наблюдателе за `#viewport-container`: «в контейнер окон добавили узел →
 * значит поехали снимки → заставку убрать». Признак ложен по построению:
 * стартовая раскладка создаёт ПУСТОЕ окно ещё до всякой загрузки —
 * `setupDefaultViewport()` на телефоне зовёт `activateViewMode('2d')`, а тот
 * создаёт `viewport-view-2d` с подписью «Нативные срезы». Именно эта подпись и
 * осталась на присланном скриншоте: окно есть, снимков нет, заставки нет.
 *
 * На чёрный экран это выродилось не сразу, и это важно понимать. Пока создание
 * стартового окна успевало пройти СИНХРОННО внутри `initUI()` — то есть до
 * `initPremiumFeatures()`, — наблюдатель ставился на уже заполненный контейнер,
 * мутаций не видел, и заставка оставалась. Появление в `activateViewMode`
 * динамического `await import('./globalSliceSlider.js')` (10.19.19, правило
 * видимости бегунка) отодвинуло создание окна за микротаск — и наблюдатель
 * начал срабатывать. Гонка, а не удаление: поэтому в списке изменений искать
 * было нечего. Датировку 10.19.19 я вывел по коду, а не сверил с архивом
 * 10.19.6 — старых исходников у меня нет, и выдавать вывод за сверку не буду.
 *
 * ПРАВИЛО. Заставка стоит, пока в программе нет СНИМКОВ, и снимается, когда они
 * появились. «Снимки появились» — это одно из:
 *   · загружена стопка (STACK_LOADED) с ненулевым числом кадров;
 *   · начата загрузка тома (VOLUME_LOAD_START) с ненулевым числом кадров;
 *   · врач выбрал серию (series:selected);
 *   · в списке серий есть хотя бы одна запись (исследование открыто).
 * Создание окна — НЕ повод. Пустая загрузка (ноль кадров) — тоже не повод:
 * снимать заставку, ничего не показав вместо неё, значит отдать врачу чёрный
 * прямоугольник, что и происходило.
 *
 * Правило живёт отдельным чистым модулем, а не строчкой в обработчике, потому
 * что по нему принимают решение ДВА места: заставка (premiumFeatures.js) и
 * запасная подсказка `#drop-placeholder` (appInit.js) — она прячется тем же
 * ложным признаком и по той же причине не видна на скриншоте.
 */

/** Поводы, по которым кто-то предлагает снять заставку. */
export const ПОВОДЫ = Object.freeze({
  /** Создано окно просмотра (раскладка). Снимков это не означает. */
  ОКНО: 'окно-создано',
  /** Загружена стопка кадров: STACK_LOADED. */
  СТОПКА: 'стопка-загружена',
  /** Начата загрузка тома: VOLUME_LOAD_START. */
  ТОМ: 'том-загружается',
  /** Врач выбрал серию в списке. */
  СЕРИЯ: 'серия-выбрана',
  /** В списке серий появились записи — исследование открыто. */
  СПИСОК: 'список-серий',
});

/**
 * Снимать ли заставку.
 *
 * @param {{повод: string, кадров?: number, серий?: number}} состояние
 * @returns {{снимать: boolean, почему: string}} причина словами — она уходит в
 *   журнал, чтобы «заставка пропала» больше не приходилось выяснять чтением.
 */
export function решениеОЗаставке(состояние = {}) {
  const повод = String(состояние.повод || '');
  const кадров = Number(состояние.кадров ?? 0);
  const серий = Number(состояние.серий ?? 0);

  switch (повод) {
    case ПОВОДЫ.ОКНО:
      // То самое место, где ломалось. Окно создаётся при старте пустым.
      return { снимать: false, почему: 'окно раскладки создано, но снимков в нём нет' };

    case ПОВОДЫ.СТОПКА:
      return кадров > 0
        ? { снимать: true, почему: `загружена стопка: кадров ${кадров}` }
        : { снимать: false, почему: 'стопка загружена пустой — показывать вместо заставки нечего' };

    case ПОВОДЫ.ТОМ:
      return кадров > 0
        ? { снимать: true, почему: `строится том: кадров ${кадров}` }
        : { снимать: false, почему: 'том без кадров — показывать вместо заставки нечего' };

    case ПОВОДЫ.СЕРИЯ:
      return { снимать: true, почему: 'врач выбрал серию' };

    case ПОВОДЫ.СПИСОК:
      return серий > 0
        ? { снимать: true, почему: `в списке серий записей: ${серий}` }
        : { снимать: false, почему: 'список серий пуст — исследование не открыто' };

    default:
      return { снимать: false, почему: `повод не распознан: «${повод || 'пусто'}»` };
  }
}

export default { ПОВОДЫ, решениеОЗаставке };
